import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion, AnimatePresence } from "framer-motion";

export default function About() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const [index, setIndex] = useState(0);

  const nextTestimonial = () => setIndex((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  const features = [
    {
      title: "Student-Focused",
      desc: "Created with students in mind — perfect for internships, job-seeking, and showing off your skills.",
      icon: "🎓",
    },
    {
      title: "Templates & Hosting",
      desc: "Pick a template, customize your content, and get it hosted instantly with optional custom domains.",
      icon: "🖼️",
    },
    {
      title: "Modern Stack",
      desc: "Powered by React and Tailwind CSS — fast, responsive, and optimized for performance.",
      icon: "⚡",
    },
  ];

  const testimonials = [
    {
      name: "Anjali S.",
      feedback: "Project Port helped me build a beautiful portfolio without writing a single line of code. Got hired in 2 weeks!",
    },
    {
      name: "Rahul V.",
      feedback: "The templates are stunning and mobile-friendly. It’s the fastest way to showcase your skills online.",
    },
    {
      name: "Deepak M.",
      feedback: "As a freelancer, this gave me a major edge. Clients loved the clean design and layout.",
    },
  ];

  return (
    <section className="bg-white dark:bg-black text-black dark:text-white px-6 py-20 min-h-screen">
      {/* HEADER */}
      <div className="max-w-4xl mx-auto text-center">
        <h1
          className="text-4xl md:text-5xl font-bold text-purple-600 dark:text-purple-400 mb-6"
          data-aos="fade-down"
        >
          About Project Port
        </h1>
        <p
          className="text-lg text-gray-700 dark:text-gray-300 mb-10"
          data-aos="fade-up"
        >
          Project Port is a platform built for students, freelancers, and early professionals who want to launch a sleek and effective portfolio in minutes. No coding required. No stress. Just powerful results.
        </p>
      </div>

      {/* FEATURES */}
      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto text-center mt-12">
        {features.map((item, i) => (
          <div
            key={i}
            className="bg-gray-100 dark:bg-gray-900 p-6 rounded-xl hover:bg-purple-100/40 dark:hover:bg-purple-800/40 transition-transform duration-300 transform hover:scale-105"
            data-aos="zoom-in"
          >
            <div className="text-5xl mb-4">{item.icon}</div>
            <h3 className="text-xl font-semibold mb-2 text-purple-600 dark:text-purple-400">{item.title}</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* MISSION */}
      <div className="max-w-4xl mx-auto text-center mt-20" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-4">Our Mission</h2>
        <p className="text-gray-700 dark:text-gray-300 text-lg">
          To empower every student and aspiring professional with a powerful online presence — one portfolio at a time.
        </p>
      </div>

      {/* CTA */}
      <div className="mt-20 text-center" data-aos="fade-up">
        <h3 className="text-2xl font-semibold mb-4 text-purple-500 dark:text-purple-300">
          Ready to build your portfolio?
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Start showcasing your skills in minutes.
        </p>
        <a
          href="/get-started"
          className="inline-block bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white px-6 py-3 rounded-full transition shadow-lg"
        >
          Get Started
        </a>
      </div>

      {/* TESTIMONIALS */}
      <div className="max-w-6xl mx-auto mt-24">
        <h2
          className="text-3xl font-bold text-center text-purple-600 dark:text-purple-400 mb-10"
          data-aos="fade-up"
        >
          What Our Users Say
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="bg-gray-100 dark:bg-gray-900 p-6 rounded-lg shadow hover:shadow-lg transition duration-300"
              data-aos="fade-up"
              data-aos-delay={i * 100}
            >
              <p className="text-gray-700 dark:text-gray-300 italic mb-4">"{t.feedback}"</p>
              <h4 className="text-purple-600 dark:text-purple-400 font-semibold">— {t.name}</h4>
            </div>
          ))}
        </div>
      </div>
       <div className="max-w-3xl mx-auto mt-24 text-center" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-10">
          What Our Users Say
        </h2>

        <div className="relative bg-gray-100 dark:bg-gray-900 p-8 rounded-xl shadow-lg">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="text-gray-700 dark:text-gray-300"
            >
              <p className="italic text-lg mb-6">"{testimonials[index].feedback}"</p>
              <h4 className="text-purple-600 dark:text-purple-400 font-semibold">— {testimonials[index].name}</h4>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          <div className="absolute top-1/2 -translate-y-1/2 left-4 right-4 flex justify-between">
            <button
              onClick={prevTestimonial}
              className="bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white w-8 h-8 rounded-full text-sm"
            >
              ‹
            </button>
            <button
              onClick={nextTestimonial}
              className="bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white w-8 h-8 rounded-full text-sm"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
